/**
 * Translates ProductStore in-memory cart state into the shared
 * `<CheckoutShell>` `Cart` value object.
 *
 * Pure function. No React, no API calls, no side effects. The Phase 3
 * adapter component composes this with `useProductCheckout()` state, then
 * hands the resulting Cart to the shell.
 *
 * Responsibilities:
 *   - One CartLine per CartItem with thumbnail, category as description,
 *     and `requiresShipping` derived from product.type.
 *   - Aggregate `needsShipping` flag = any line requires shipping.
 *   - All other discount-related metadata flags are false (products don't
 *     have vouchers, promo codes, or admin discounts today).
 *   - Subtotal = total = sum(qty × unitPrice). No discount lines today.
 *
 * What it does NOT do:
 *   - Validate input. Garbage in = garbage out.
 *   - Network. The adapter calls cart-update APIs and re-runs this on the
 *     refreshed CartItem[].
 */

import { Cart, CartLine } from "@/modules/checkout/types";
import type { CartItem } from "../api/fetchCart/types";

export type BuildProductCartInput = {
  cartItems: CartItem[];
};

const buildLine = (item: CartItem): CartLine => ({
  sku: item.product.id,
  name: item.product.name,
  description: item.product.category?.name,
  quantity: item.quantity,
  unitPrice: parseFloat(item.product.price) || 0,
  imageUrl: item.product.thumbnailUrl ?? undefined,
  metadata: {
    requiresShipping: item.product.type === "physical",
    voucherEligible: false,
    discountCodeEligible: false,
  },
  domainData: {
    cartItemId: item.id,
    productId: item.product.id,
    productType: item.product.type,
  },
});

export function buildProductCart(input: BuildProductCartInput): Cart {
  const lines = input.cartItems.map(buildLine);
  const subtotal = lines.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0);

  return {
    lines,
    discountLines: [],
    subtotal,
    total: subtotal,
    needsShipping: lines.some((l) => l.metadata.requiresShipping),
    acceptsDiscountCode: false,
    acceptsVouchers: false,
    adminCanDiscount: false,
  };
}
